// Edit an existing SKU grant — partial update.
// Endpoint: PATCH /api/admin/licensing/orgs/:org_id/skus/:sku_id

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  MenuItem,
  Switch,
  FormControlLabel,
  Select} from "@mui/material";
import { FormDrawer, TOKENS, useDrawerBaselineDirty } from "../design";
import { ReactComponent as EditIcon } from "../../../../../vendor/_new/icon/sites/edit.svg";
import { updateSku } from "../../../../../services/licensingService";
import { SafeTextField } from "../../../../common/Greens";
import { isExpiryDateBeforeToday, todayDateString } from "../../../../common/layerdDateTimePicker/utils";

const BYTE_UNITS = [
  { value: 1, label: "B" },
  { value: 1024, label: "KB" },
  { value: 1024 * 1024, label: "MB" },
  { value: 1024 * 1024 * 1024, label: "GB" },
  { value: 1024 * 1024 * 1024 * 1024, label: "TB" },
];

const fieldLabelSx = {
  fontSize: 12,
  fontWeight: 600,
  color: TOKENS.fg2,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  mb: 0.5,
};

const helperSx = { fontSize: 11.5, color: TOKENS.fg3, mt: 0.5 };

// Decompose a byte count into the largest tidy unit (so e.g. 1073741824 → 1 GB).
const decomposeBytes = (n) => {
  if (n == null) return { val: "", unit: 1024 * 1024 * 1024 };
  let i = BYTE_UNITS.length - 1;
  while (i > 0 && n % BYTE_UNITS[i].value !== 0) i--;
  return { val: String(n / BYTE_UNITS[i].value), unit: BYTE_UNITS[i].value };
};

const M_EditSku = ({ open, orgId, orgTitle, sku, onClose, onSaved }) => {
  const [isTrial, setIsTrial] = useState(false);
  const [expiry, setExpiry] = useState("");
  const [reqLimit, setReqLimit] = useState("");
  const [byteLimit, setByteLimit] = useState("");
  const [byteUnit, setByteUnit] = useState(1024 * 1024 * 1024);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [baselineKey, setBaselineKey] = useState(0);

  // Initial state — captured so we only PATCH fields that actually changed.
  const initial = useMemo(() => {
    if (!sku) return null;
    const isoDay = sku.expiry_date ? new Date(sku.expiry_date).toISOString().slice(0, 10) : "";
    const dec = decomposeBytes(sku.volume_limit_bytes);
    return {
      isTrial: !!sku.is_trial,
      expiry: isoDay,
      reqLimit: sku.volume_limit_requests == null ? "" : String(sku.volume_limit_requests),
      byteLimitVal: dec.val,
      byteUnit: dec.unit,
      notes: sku.notes || "",
    };
  }, [sku]);

  useEffect(() => {
    if (!open || !initial) return;
    setIsTrial(initial.isTrial);
    setExpiry(initial.expiry);
    setReqLimit(initial.reqLimit);
    setByteLimit(initial.byteLimitVal);
    setByteUnit(initial.byteUnit);
    setNotes(initial.notes);
    setSaving(false);
    setErr(null);
    setBaselineKey((k) => k + 1);
  }, [open, initial]);

  const formSnapshot = useMemo(
    () => ({
      isTrial,
      expiry,
      reqLimit,
      byteLimit,
      byteUnit,
      notes,
    }),
    [isTrial, expiry, reqLimit, byteLimit, byteUnit, notes],
  );
  const isDirty = useDrawerBaselineDirty(open, formSnapshot, baselineKey);

  const canSubmit = !!sku && !!expiry && !saving;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (expiry !== initial.expiry && isExpiryDateBeforeToday(expiry)) {
      setErr("Expiry date must be today or in the future");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const body = {};
      if (isTrial !== initial.isTrial) body.is_trial = isTrial;
      if (expiry !== initial.expiry) {
        body.expiry_date = new Date(`${expiry}T00:00:00.000Z`).toISOString();
      }
      // requests
      const r = reqLimit === "" ? null : Number(reqLimit);
      const initR = initial.reqLimit === "" ? null : Number(initial.reqLimit);
      if (r !== initR) body.volume_limit_requests = r;
      // bytes
      const b = byteLimit === "" ? null : Number(byteLimit) * Number(byteUnit);
      const initB =
        initial.byteLimitVal === "" ? null : Number(initial.byteLimitVal) * Number(initial.byteUnit);
      if (b !== initB) body.volume_limit_bytes = b;
      // notes
      const n = notes.trim() === "" ? null : notes;
      if (n !== (initial.notes === "" ? null : initial.notes)) body.notes = n;

      if (Object.keys(body).length === 0) {
        // Nothing changed — just close.
        onSaved?.();
        return;
      }

      await updateSku(orgId, sku.sku_id, body);
      onSaved?.();
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Failed to update SKU");
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDrawer
      open={open}
      onClose={onClose}
      title={`Edit ${sku?.sku_name || sku?.sku_id || "SKU"}`}
      subtitle="Update grant parameters"
      icon={<EditIcon />}
      submitLabel="Save changes"
      onSubmit={handleSubmit}
      canSubmit={canSubmit}
      loading={saving}
      isDirty={isDirty}
    >
      <Stack spacing={2.5}>
        <Typography sx={{ fontSize: 12.5, color: TOKENS.fg2 }}>
          Editing the {sku?.sku_name || sku?.sku_id} grant for <strong>{orgTitle}</strong>.
        </Typography>

        <FormControlLabel
          sx={{ ml: 0, "& .MuiFormControlLabel-label": { fontSize: 13, color: TOKENS.fg1 } }}
          control={<Switch size="small" checked={isTrial} onChange={(e) => setIsTrial(e.target.checked)} />}
          label="Trial grant"
          labelPlacement="end"
        />

        <Box>
          <Typography sx={fieldLabelSx}>Expiry date</Typography>
          <SafeTextField
            type="date"
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
            size="small"
            fullWidth
            inputProps={{ min: todayDateString() }}
            InputProps={{ sx: { fontSize: 13 } }}
          />
        </Box>

        <Box>
          <Typography sx={fieldLabelSx}>Request quota</Typography>
          <SafeTextField
            type="number"
            value={reqLimit}
            onChange={(e) => setReqLimit(e.target.value)}
            placeholder="Unlimited"
            size="small"
            fullWidth
            inputProps={{ min: 1 }}
            InputProps={{ sx: { fontSize: 13 } }}
          />
          <Typography sx={helperSx}>Blank = unlimited.</Typography>
        </Box>

        <Box>
          <Typography sx={fieldLabelSx}>Byte quota</Typography>
          <Stack direction="row" spacing={1}>
            <SafeTextField
              type="number"
              value={byteLimit}
              onChange={(e) => setByteLimit(e.target.value)}
              placeholder="Unlimited"
              size="small"
              sx={{ flex: 1 }}
              inputProps={{ min: 0.0001, step: "any" }}
              InputProps={{ sx: { fontSize: 13 } }}
            />
            <Select
              value={byteUnit}
              onChange={(e) => setByteUnit(Number(e.target.value))}
              size="small"
              sx={{ minWidth: 84, "& .MuiOutlinedInput-input": { fontSize: 13 } }}
              disabled={byteLimit === ""}
            >
              {BYTE_UNITS.map((u) => (
                <MenuItem key={u.label} value={u.value} sx={{ fontSize: 13 }}>{u.label}</MenuItem>
              ))}
            </Select>
          </Stack>
          <Typography sx={helperSx}>Blank = unlimited.</Typography>
        </Box>

        <Box>
          <Typography sx={fieldLabelSx}>Notes</Typography>
          <SafeTextField
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional"
            size="small"
            fullWidth
            multiline
            minRows={3}
            inputProps={{ maxLength: 2048 }}
            InputProps={{ sx: { fontSize: 13 } }}
          />
        </Box>

        {err ? (
          <Box
            sx={{
              p: 1.25,
              borderRadius: `${TOKENS.radiusXs}px`,
              background: TOKENS.dangerBg,
              border: `1px solid ${TOKENS.dangerBorder}`,
              color: TOKENS.dangerInk,
              fontSize: 12.5,
            }}
          >
            {err}
          </Box>
        ) : null}
      </Stack>
    </FormDrawer>
  );
};

export default M_EditSku;
